<template>
  <div class="subscription-form-container">
    <div class="section-container">
      <substrate-mount>
        <div class="textarea-composite-container">
          <input-textarea
            v-model="bill.description"
            input-id="bill-description"
            :input-name="$t('common.description')"
            :input-title="$t('enter.description')"
            :input-placeholder="$t('what.paid')"
            :max-length="510"
            :required="false"
            :rows="1"
            composite
          />
          <attachment-image
            :link="bill.attachment.link"
            input-id="bill.attachment"
            type="secondary"
            composite
            composite-textarea
            @loading="onImageLoading"
            @uploaded="data => bill.attachment.setData(data)"
            @reset="bill.attachment.reset()"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.isMulti"
            :label="$t('allow.multiple.payments')"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.useTariffName"
            :label="$t('variants.name')"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.useTariffDescription"
            :label="$t('variants.description')"
          />
        </div>
        <div v-if="periodicRecurrentPaymentPermission" class="switcher-container">
          <cs-switcher
            v-model="bill.useSubscription"
            :label="$t('organize.subscription')"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.isContentUrl"
            :label="$t('organize.content.delivery')"
          />
        </div>
      </substrate-mount>
    </div>

    <div
      v-for="(tariff, idx) in bill.billTariffs"
      :key="`tariff-${idx}`"
      class="section-container"
    >
      <template v-if="isDesktop">
        <form-movement-control
          v-if="isSeveralTariffsAvailable"
          :inactive-up="isInactiveMovementUp(idx)"
          :inactive-down="isInactiveMovementDown(idx)"
          @movementUp="onMovementUp(idx)"
          @movementDown="onMovementDown(idx)"
        />
        <form-delete-icon
          v-if="isSeveralTariffsAvailable"
          @click="onRemoveTariff(idx)"
        />
      </template>
      <substrate-mount>
        <div v-if="bill.useTariffName" class="input-container">
          <input-textarea
            v-model="tariff.name"
            :input-id="`tariff-name-${idx}`"
            :input-name="$t('common.name')"
            :input-title="$t('enter.title')"
            :input-placeholder="$t('for.example.base')"
            :max-length="255"
            :required="false"
            :rows="1"
          />
        </div>
        <div v-if="bill.useTariffDescription" class="input-container">
          <input-textarea
            v-model="tariff.description"
            :input-id="`tariff-description-${idx}`"
            :input-name="$t('common.description')"
            :input-title="$t('enter.description')"
            :input-placeholder="$t('what.tariff.give')"
            :max-length="255"
            :required="false"
            :rows="1"
          />
        </div>
        <labeled-input
          v-model="tariff.amount"
          :label="$t('amount.for.pay', { getCurrencySign: currency.sign })"
          :placeholder="`0 ${currency.sign}`"
          :error="$v.bill.billTariffs.$each[idx].amount.$error"
          :force-replace="[
            [/[^0-9\.\,]+/g, ''],
            [/^\./g, ''],
            [/\,/, '\.'],
          ]"
          :force-format="[$formatInputAmount({
            significantZeros: currency.significant_zeros,
            decimalsSeparator: '.',
            thousandSeparator: ' ',
          })]"
          @focus="$v.bill.billTariffs.$each[idx].amount.$reset"
          @blur="$v.bill.billTariffs.$each[idx].amount.$touch"
        />
        <template v-if="bill.useSubscription">
          <div class="option-container" @click="onShownWriteOffOptions(tariff.writeOffOption, idx)">
            <option-panel :option="getPreparedWriteOffOption(tariff.writeOffOption)" noactive>
              <cs-button type="secondary">
                {{ $t('common.change') }}
              </cs-button>
            </option-panel>
          </div>
          <div class="switcher-container">
            <cs-switcher
              v-model="tariff.oneTimePayment"
              :label="$t('allow.payment.without.subscription')"
            />
          </div>
          <template v-if="isShownTrialDaysSwitcher(tariff)">
            <div class="switcher-container">
              <cs-switcher
                v-model="tariff.useTrialDays"
                :label="$t('subscription.form.trial.days.swicther.label')"
              />
            </div>
            <div v-if="tariff.useTrialDays" class="input-container">
              <input-number
                v-model="tariff.trialDays"
                :input-name="$t('subscription.form.trial.days.input.label')"
                :input-title="$t('subscription.form.trial.days.input.label')"
                input-placeholder="0"
                :input-id="`tariff-trial-days-${idx}`"
                :show-counter="false"
                :min-value="1"
                :max-value="365"
                :required="false"
              >
                <help-link slot="link" source="https://help.capusta.space/bill/trial_subscription" />
              </input-number>
            </div>
          </template>
        </template>
        <div v-if="bill.isContentUrl" class="input-container">
          <input-link-or-iframe
            ref="tariffContentUrl"
            v-model="tariff.contentUrl"
            :input-id="`tariff-content-url-${idx}`"
            :input-name="$t('link.to.content.or.code.for.embedding')"
            input-placeholder="https://mysite.ru/myfile.pdf"
            capusta-info="billIframe"
            @showInfo="$emit('showInfo')"
          />
        </div>
        <template v-if="!isDesktop">
          <div v-if="isSeveralTariffsAvailable" class="controls-container">
            <form-movement-control
              :inactive-up="isInactiveMovementUp(idx)"
              :inactive-down="isInactiveMovementDown(idx)"
              @movementUp="onMovementUp(idx)"
              @movementDown="onMovementDown(idx)"
            />
            <form-delete-icon @click="onRemoveTariff(idx)" />
          </div>
        </template>
      </substrate-mount>
    </div>

    <div v-if="!isExceededTariffsCount" class="panel-container">
      <capusta-panel
        icon="circle-plus"
        :description="$t('add.variant')"
        @click="onAddTariff"
      />
    </div>

    <div class="button-primary-container">
      <cs-button :disabled="!isValid" @click="onSubmit">
        {{ $t('get.link') }}
      </cs-button>
    </div>

    <div class="button-secondary-container">
      <cs-button type="secondary" :disabled="!isValid" @click="onSubmitWithQrCode">
        <span class="icon">
          <cs-icon name="qr-code" size="extra-medium" />
        </span>
        {{ $t('get.qr') }}
      </cs-button>
    </div>

    <reusable-dialog v-model="isShownWriteOffOptions">
      <div
        v-for="option in getPreparedWriteOffOptions"
        :key="option.id"
        :class="$style.periodicity"
      >
        <option-panel
          :option="option"
          @click="onSelectWriteOffOption"
        />
      </div>
    </reusable-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import attachmentMixin from 'lib/mixins/attachmentMixin';

import { isLessMaxAmount, isMoreMinAmount } from '@/components/inputElements/CustomValidators';

import CapustaPanel from 'modules/CapustaLayout/CapustaPanel';
import SubstrateMount from '@/components/capusta/SubstrateMount';
import FormDeleteIcon from '@/components/capusta/FormDeleteIcon';
import FormMovementControl from '@/components/capusta/FormMovementControl';
import OptionPanel from '@/components/capusta/OptionPanel';

import InputTextarea from '@/components/inputElements/InputTextarea';
import InputLinkOrIframe from '@/components/inputElements/InputLinkOrIframe';
import InputNumber from '@/components/inputElements/InputNumber';

import AttachmentImage from 'modules/AttachmentImage';

import HelpLink from 'molecules/HelpLink';
import LabeledInput from 'molecules/common/LabeledInput';

import TariffForm from '../classes/TariffForm';

import { MAX_TARIFFS } from '../const';

export default {
  name: 'AdvancedTariffInvoicing',
  mixins: [
    attachmentMixin,
  ],
  components: {
    CapustaPanel,
    SubstrateMount,
    FormDeleteIcon,
    FormMovementControl,
    OptionPanel,
    InputTextarea,
    InputLinkOrIframe,
    InputNumber,
    AttachmentImage,
    HelpLink,
    LabeledInput,
  },
  props: {
    transaction: Object,
  },
  mounted() {
    this.fillingData();
  },
  data() {
    return {
      targetIdx: null,
      bill: new TariffForm,
      isShownWriteOffOptions: false,
    };
  },
  validations() {
    return {
      bill: {
        billTariffs: {
          $each: {
            amount: {
              minValue: isMoreMinAmount(this.minLimitAmount, this.currency),
              maxValue: isLessMaxAmount(this.maxLimitAmount, 0, this.currency),
            },
          },
        },
      },
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    isValid() {
      if (this.submittingDisabled) {
        return false;
      }

      if (this.bill.isContentUrl) {
        return this.isValidContentUrl && this.isValidAmount && this.isValidTrialDays;
      }

      return this.isValidAmount && this.isValidTrialDays;
    },
    isValidAmount() {
      const { billTariffs } = this.bill;

      if (billTariffs.every(tariff => this.$validate.isEmptyString(tariff.amount))) {
        return true;
      }

      return !this.$v.bill.$invalid;
    },
    isValidContentUrl() {
      const { billTariffs } = this.bill;
      if (billTariffs.some(tariff => this.$validate.isNull(tariff.contentUrl) || this.$validate.isEmptyString(tariff.contentUrl))) {
        return false;
      }

      return billTariffs.every(tariff => this.$validate.isNotNull(tariff.amount)) && this.$refs.tariffContentUrl && this.$refs.tariffContentUrl.every(value => !value.$v.$error);
    },
    isValidTrialDays() {
      const { billTariffs } = this.bill;

      const isValidVariants = billTariffs.every(tariff => {
        const { useTrialDays, trialDays, writeOffOption } = tariff;
        const { periodicity } = writeOffOption;

        if (!useTrialDays || periodicity === 'SELECTIVELY') {
          return true;
        }

        if (tariff.oneTimePayment) {
          return true;
        }

        if (this.$validate.isEmptyString(trialDays)) {
          return false;
        }

        return trialDays > 0 && trialDays <= 365;
      });

      return isValidVariants;
    },
    isSeveralTariffsAvailable() {
      return this.bill.billTariffs.length > 1;
    },
    isExceededTariffsCount() {
      return this.bill.billTariffs.length >= MAX_TARIFFS;
    },
    minLimitAmount() {
      if (this.bill.useSubscription) {
        return this.limits.min_recurrent_amount;
      }

      return this.limits.min_amount;
    },
    maxLimitAmount() {
      if (this.bill.useSubscription) {
        return this.limits.max_recurrent_amount;
      }

      return this.limits.max_amount;
    },
    getPreparedWriteOffOptions() {
      return this.writeOffOptions.map(this.getPreparedWriteOffOption);
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters(['isDesktop', 'periodicRecurrentPaymentPermission']),
    ...mapGetters('bills', ['limits', 'writeOffOptions']),
  },
  methods: {
    getPreparedWriteOffOption(option) {
      return {
        ...option,
        description: this.$t(option.description),
        label: this.$t(option.label),
      };
    },
    onSubmitWithQrCode() {
      this.onSubmit(true);
    },
    onSubmit(useQrCode = false) {
      if (!this.isValid) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.$emit('submit', {
        bill: this.bill.getData(),
        qrCode: useQrCode,
      });
    },
    isInactiveMovementUp(idx) {
      return idx === 0;
    },
    isInactiveMovementDown(idx) {
      return idx === this.bill.billTariffs.length - 1;
    },
    onMovementUp(idx) {
      if (idx <= 0) {
        return;
      }

      this.bill.tariffUp(idx);
    },
    onMovementDown(idx) {
      if (idx >= this.bill.billTariffs.length - 1) {
        return;
      }

      this.bill.tariffDown(idx);
    },
    onShownWriteOffOptions(writeOffOption, idx) {
      this.targetIdx = idx;
      this.isShownWriteOffOptions = !this.isShownWriteOffOptions;

      this.ACTIVE_WRITE_OFF_OPTION(writeOffOption);
    },
    onSelectWriteOffOption(option) {
      const preparedOption = Object.assign(option, {
        label: this.$t('subscription.period'),
        active: true,
      });

      this.bill.setWriteOffOption(preparedOption, this.targetIdx);
      this.isShownWriteOffOptions = false;
      this.targetIdx = null;

      this.ACTIVE_WRITE_OFF_OPTION(preparedOption);
    },
    onAddTariff() {
      if (this.isExceededTariffsCount) {
        return;
      }

      this.bill.addTariff();
    },
    onRemoveTariff(idx) {
      this.bill.deleteTariff(idx);
      // TODO: багфикс для обновления $refs и корректной валидации
      this.$nextTick(() => {
        this.bill.billTariffs = this.bill.billTariffs.map(tariff => tariff);
      });
    },
    isShownTrialDaysSwitcher(tariff) {
      const { writeOffOption, oneTimePayment } = tariff;

      if (writeOffOption.periodicity === 'SELECTIVELY') {
        return false;
      }

      if (oneTimePayment) {
        return false;
      }

      return true;
    },
    fillingData() {
      this.bill.currency = this.currency;
    },
    ...mapMutations('bills', ['ACTIVE_WRITE_OFF_OPTION']),
  },
};
</script>

<style lang="sass" module>
  .periodicity
    &:not(:last-child)
      margin-bottom: 10px
</style>

<style lang="sass" scoped>
  .section-container
    position: relative
    margin-bottom: 20px

    & + .button-primary-container
      margin-top: 40px

  .input-container
    .input,
    .textarea
      margin: 0

      a
        height: auto
        margin: 0
        padding: 0
        align-self: auto
        text-decoration: none
        position: absolute
        right: 20px

        &:after
          content: none

  .textarea-composite-container
    position: relative
    display: flex
    align-items: center

    .textarea
      margin: 0

  .panel-container
    margin: 40px 0
    padding: 0 20px

  .controls-container
    display: flex
    justify-content: space-between

  .button-primary-container
    display: flex
    justify-content: center
    margin-bottom: 20px

  .button-secondary-container
    display: flex
    justify-content: center

    & .icon
      margin-right: 10px
</style>

